import { inject, Injectable } from '@angular/core';
import { LoadingStore } from '../stores/loading.store';

interface LoadingRequest {
  id: string;
  type: 'navigation' | 'http';
  startTime: number;
}

@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly store = inject(LoadingStore);
  readonly loading = this.store.loading;
  readonly error = this.store.error;
  readonly progressValue = this.store.progressValue;
  readonly progressMode = this.store.progressMode;
  readonly loadingMessageKey = this.store.loadingMessageKey;

  private readonly config = {
    minimumLoadingTime: 300,
    completionDelay: 200,
  };

  private readonly activeRequests = new Map<string, LoadingRequest>();

  startLoading() {
    this.store.startLoading();
  }

  stopLoading(error: string | null = null) {
    this.store.stopLoading(error);
  }

  setLoading(isLoading: boolean) {
    this.store.setLoading(isLoading);
  }

  setError(error: string | null) {
    this.store.setError(error);
  }

  startHttpRequest(requestId: string): void {
    this.addRequest({
      id: requestId,
      type: 'http',
      startTime: Date.now(),
    });
  }

  completeHttpRequest(requestId: string, error?: string): void {
    this.removeRequest(requestId, error);
  }

  startNavigation(navigationId: string): void {
    this.addRequest({
      id: `nav_${navigationId}`,
      type: 'navigation',
      startTime: Date.now(),
    });
  }

  completeNavigation(navigationId: string, error?: string): void {
    this.removeRequest(`nav_${navigationId}`, error);
  }

  createRequestId = (operation: string, details: string = '') => `${operation}_${details}_${Date.now()}`;

  private addRequest(request: LoadingRequest): void {
    this.activeRequests.set(request.id, request);
    this.updateLoadingState();

    if (request.type === 'http') {
      this.store.updateProgress('loading');
    }
  }

  private removeRequest(requestId: string, error?: string): void {
    const request = this.activeRequests.get(requestId);
    if (!request) return;

    const elapsedTime = Date.now() - request.startTime;
    const remainingTime = Math.max(0, this.config.minimumLoadingTime - elapsedTime);

    setTimeout(() => {
      this.activeRequests.delete(requestId);
      this.updateLoadingState(error);
    }, remainingTime);
  }

  private updateLoadingState(error?: string): void {
    const hasRequests = this.activeRequests.size > 0;

    if (!hasRequests) {
      this.store.updateProgress('completing');
      setTimeout(() => {
        this.store.completeRequest(error ?? null);

        this.store.updateProgress('idle');
      }, this.config.completionDelay);
    } else {
      if (!this.store.loading()) {
        this.store.startRequest();
      }

      this.store.updateProgress('loading');
    }
  }
}
